import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Star, Upload, X } from 'lucide-react';
import { testimonialsAPI, uploadAPI } from '@/lib/api';

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    id: '',
    name_fa: '',
    service_type: 'مهاجرت تحصیلی',
    destination_country: '',
    rating: 5,
    comment_fa: '',
    image_url: '',
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await testimonialsAPI.getAll();
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      setFormData({ ...formData, image_url: response.data.url });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('آپلود تصویر با خطا مواجه شد');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingTestimonial) {
        await testimonialsAPI.update(formData.id, formData);
        alert('نظر با موفقیت به‌روزرسانی شد');
      } else {
        const newId = `testimonial_${Date.now()}`;
        await testimonialsAPI.create({ ...formData, id: newId });
        alert('نظر با موفقیت ایجاد شد');
      }
      
      fetchTestimonials();
      closeModal();
    } catch (error) {
      console.error('Failed to save testimonial:', error);
      alert('خطا در ذخیره نظر');
    }
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData(testimonial);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این نظر اطمینان دارید؟')) return;

    try {
      await testimonialsAPI.delete(id);
      alert('نظر با موفقیت حذف شد');
      fetchTestimonials();
    } catch (error) {
      console.error('Failed to delete testimonial:', error);
      alert('خطا در حذف نظر');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTestimonial(null);
    setFormData({
      id: '',
      name_fa: '',
      service_type: 'مهاجرت تحصیلی',
      destination_country: '',
      rating: 5,
      comment_fa: '',
      image_url: '',
    });
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  return (
    <div className="min-h-screen bg-black p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">مدیریت نظرات مشتریان</h1>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F3E5AB] transition-colors"
          >
            <Plus size={20} />
            افزودن نظر جدید
          </button>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className="glass-effect border border-white/10 rounded-xl p-6">
              {/* Customer Image */}
              {testimonial.image_url && (
                <div className="flex justify-center mb-4">
                  <img
                    src={testimonial.image_url}
                    alt={testimonial.name_fa}
                    className="w-20 h-20 rounded-full object-cover border-2 border-[#D4AF37]"
                  />
                </div>
              )}

              {/* Customer Info */}
              <h3 className="text-lg font-semibold text-white text-center mb-1">{testimonial.name_fa}</h3>
              <p className="text-sm text-zinc-400 text-center mb-2">{testimonial.service_type}</p>
              <p className="text-xs text-zinc-500 text-center mb-3">{testimonial.destination_country}</p>

              {/* Rating */}
              <div className="flex justify-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={`star-${testimonial.id}-${i}`}
                    size={16}
                    className={i < testimonial.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-zinc-600'}
                  />
                ))}
              </div>

              {/* Comment */}
              <p className="text-sm text-zinc-300 text-center mb-4 line-clamp-3">{testimonial.comment_fa}</p>

              {/* Actions */}
              <div className="flex gap-2 justify-center">
                <button
                  onClick={() => handleEdit(testimonial)}
                  className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDelete(testimonial.id)}
                  className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {testimonials.length === 0 && (
          <div className="text-center text-zinc-400 py-12">
            هنوز نظری ثبت نشده است. برای افزودن نظر جدید روی دکمه بالا کلیک کنید.
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingTestimonial ? 'ویرایش نظر' : 'افزودن نظر جدید'}
                </h2>
                <button onClick={closeModal} className="text-zinc-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">تصویر مشتری (اختیاری)</label>
                  {formData.image_url ? (
                    <div className="relative">
                      <img src={formData.image_url} alt="Preview" className="w-32 h-32 rounded-full object-cover mx-auto border-2 border-[#D4AF37]" />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_url: '' })}
                        className="absolute top-0 right-1/2 transform translate-x-16 bg-red-600 text-white p-1 rounded-full"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="testimonial-image"
                      />
                      <label htmlFor="testimonial-image" className="cursor-pointer">
                        <Upload className="mx-auto mb-2 text-zinc-400" size={32} />
                        <p className="text-sm text-zinc-400">
                          {uploading ? 'در حال آپلود...' : 'کلیک کنید یا تصویر را بکشید'}
                        </p>
                      </label>
                    </div>
                  )}
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">نام مشتری *</label>
                  <input
                    type="text"
                    value={formData.name_fa}
                    onChange={(e) => setFormData({ ...formData, name_fa: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    required
                  />
                </div>

                {/* Service Type */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">نوع خدمت *</label>
                  <select
                    value={formData.service_type}
                    onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    required
                  >
                    <option value="مهاجرت تحصیلی">مهاجرت تحصیلی</option>
                    <option value="مهاجرت کاری">مهاجرت کاری</option>
                    <option value="ویزای توریستی">ویزای توریستی</option>
                    <option value="اخذ پذیرش">اخذ پذیرش</option>
                    <option value="مشاوره">مشاوره</option>
                  </select>
                </div>

                {/* Destination Country */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">کشور مقصد *</label>
                  <input
                    type="text"
                    value={formData.destination_country}
                    onChange={(e) => setFormData({ ...formData, destination_country: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    placeholder="مثال: کانادا"
                    required
                  />
                </div>

                {/* Rating */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">امتیاز (از 5) *</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData({ ...formData, rating: star })}
                        className="p-2"
                      >
                        <Star
                          size={32}
                          className={star <= formData.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-zinc-600'}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Comment */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">متن نظر *</label>
                  <textarea
                    value={formData.comment_fa}
                    onChange={(e) => setFormData({ ...formData, comment_fa: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#D4AF37] min-h-[120px]"
                    required
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F3E5AB] transition-colors font-medium"
                  >
                    {editingTestimonial ? 'به‌روزرسانی' : 'ایجاد'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-6 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors"
                  >
                    انصراف
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTestimonials;
